import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const LazyImage = ({
  src,
  alt,
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2NjYyIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9hZGluZy4uLjwvdGV4dD48L3N2Zz4=',
  priority,
  width,
  height,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px', // Start loading 50px before image comes into view
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    // console.log('LazyImage loaded successfully for src:', src);
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    // console.log('LazyImage error for src:', src);
    setHasError(true);
    setIsLoaded(false);
  };

  return (
    <ImageContainer
      ref={imgRef}
      style={{
        width: width || '100%',
        height: height || '100%',
      }}
    >
      {hasError ? (
        <ErrorPlaceholder>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>Failed to load image</ErrorText>
        </ErrorPlaceholder>
      ) : (
        <StyledImage
          src={isInView ? src : placeholder}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          $isLoaded={isLoaded}
          loading="lazy"
          decoding="async"
          {...(priority && { fetchpriority: 'high' })}
          {...props}
        />
      )}
    </ImageContainer>
  );
};

export default LazyImage;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  transition: opacity 0.3s ease;
  opacity: ${props => (props.$isLoaded ? 1 : 0.7)};
  display: block;
  position: absolute;
  top: 0;
  left: 0;

  &:hover {
    transform: scale(1.02);
    transition: transform 0.3s ease;
  }
`;

const ErrorPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f0f0f0;
  color: #666;
  border-radius: inherit;
`;

const ErrorIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 8px;
`;

const ErrorText = styled.div`
  font-size: 0.9rem;
  text-align: center;
`;
